import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppQuickNav from '../components/AppQuickNav';   // 🧭 швидкий перехід по розділах вкладки
import {
  Box, Typography, Paper, Button, Chip, Stack, Alert, Badge,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Switch, TextField, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Link, Tabs, Tab,
} from '@mui/material';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` });
const JSON_AUTH = () => ({ ...AUTH(), 'Content-Type': 'application/json' });
const API = (path, opts = {}) =>
  fetch('/api/dashboard/numeriq' + path, { headers: AUTH(), ...opts });
const APIJ = (path, body, method = 'POST') =>
  fetch('/api/dashboard/numeriq' + path, { method, headers: JSON_AUTH(), body: JSON.stringify(body) });

// ── 💌 Запрошення «Поділитися» (04.10.2026) ──
// Так само, як у ZODIAQ: другові приходить картинка, під нею текст і кнопка «Спробувати».
// Текст і кнопка — окремо для кожної мови; картинка: своя для мови → спільна → намальована.
const NQ_SHARE_LANGS = [['uk', 'UA'], ['en', 'EN'], ['pl', 'PL'], ['de', 'DE'], ['fr', 'FR'], ['it', 'IT'], ['es', 'ES'], ['ru', 'RU']];
const nqLangName = (scope) => (scope === 'all' ? 'усіх мов' : ((NQ_SHARE_LANGS.find(([l]) => l === scope) || [])[1] || scope));

// Картинки NUMERIQ — лише через проксі дашборду: CSP сайту не пускає numeriq.mediabot.uk.
function NqAuthImg({ src, style, fallback }) {
  const [url, setUrl] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    let alive = true; let ou = null;
    setBad(false); setUrl(null);
    fetch(src, { headers: AUTH() })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => { if (!alive) return; ou = URL.createObjectURL(b); setUrl(ou); })
      .catch(() => { if (alive) setBad(true); });
    return () => { alive = false; if (ou) URL.revokeObjectURL(ou); };
  }, [src]);
  if (bad) return fallback || null;
  if (!url) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>;
  return <img src={url} alt="" style={style} />;
}

// NUMERIQ не має Pillow — тож зменшуємо тут, у браузері: ≤1280 px, JPEG.
function nqShrinkImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const u = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, 1280 / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(u);
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas'))), 'image/jpeg', 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(u); reject(new Error('Це не картинка')); };
    img.src = u;
  });
}

// 🎁 Реферальний бонус (05.10.2026, «як у ZODIAQ»). Значення — у numeriq_settings через
// наявний проксі /settings. ⚠️ Перемикач підписано тим, що він РЕАЛЬНО вимикає: у ZODIAQ
// перемикач, підписаний як «бонусні дні Преміуму», мовчки вимикав і токени за друзів.
// 👥 Хто кого привів і скільки на цьому заробив (05.10.2026, прохання OWNER).
// Суми — з книги нарахувань, а не з прапорців: «оплачено» означає, що токени справді нараховано.
const NqRefPairs_STATUS = {
  paid: ['оплачено', '#2e5a32'], pending: ['ще не нараховано', '#444'],
  flagged: ['⚠️ підозра — вирішити нижче', '#7a3a2a'], rejected: ['відхилено', '#555'],
};
function NqRefPairs({ summary, pairs, totals, unit, pendingHint }) {
  const [open, setOpen] = useState(false);
  const sm = summary || [], pr = pairs || [];
  const dt = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('.') : '');
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ color: '#ddd', fontWeight: 700, fontSize: 14, mb: .5 }}>👥 Хто кого привів</Typography>
      {pr.length === 0 ? <Typography sx={{ color: '#777', fontSize: 13 }}>Поки ніхто нікого не привів.</Typography> : (<>
        <Typography sx={{ color: '#999', fontSize: 12.5, mb: 1 }}>
          Усього: <b style={{ color: '#ddd' }}>{(totals && totals.pairs) ?? pr.length}</b> запрошених ·
          запрошувачам нараховано <b style={{ color: '#ddd' }}>{(totals && totals.earned) ?? 0} {unit}</b> ·
          новачкам <b style={{ color: '#ddd' }}>{(totals && totals.invited_bonus) ?? 0} {unit}</b>
        </Typography>
        <Box sx={{ border: '1px solid rgba(150,130,255,.18)', borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
          <Box sx={{ display: 'flex', px: 1.2, py: .6, bgcolor: 'rgba(124,92,255,.12)', fontSize: 12, color: '#aaa', gap: 1 }}>
            <Box sx={{ flex: 1 }}>Хто запрошував</Box><Box sx={{ width: 90, textAlign: 'right' }}>Привів</Box><Box sx={{ width: 110, textAlign: 'right' }}>Заробив</Box>
          </Box>
          {sm.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', px: 1.2, py: .6, borderTop: '1px solid rgba(150,130,255,.1)', fontSize: 13, color: '#ddd', gap: 1 }}>
              <Box sx={{ flex: 1, wordBreak: 'break-word' }}>{s.referrer_name || s.referrer_tid}</Box>
              <Box sx={{ width: 90, textAlign: 'right' }}>{s.invited}</Box>
              <Box sx={{ width: 110, textAlign: 'right', color: s.earned > 0 ? '#39e58c' : '#888', fontWeight: 600 }}>+{s.earned} {unit}</Box>
            </Box>
          ))}
        </Box>
        <Button size="small" onClick={() => setOpen(!open)} sx={{ color: '#9fd', fontSize: 12, px: 0 }}>
          {open ? '▲ Сховати' : '▼ Показати'} усі запрошення ({pr.length})
        </Button>
        {open && pr.map((p, i) => {
          const st = NqRefPairs_STATUS[p.status] || NqRefPairs_STATUS.pending;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: .6, borderBottom: '1px solid rgba(150,130,255,.1)', flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#888', fontSize: 12, width: 72 }}>{dt(p.created_at)}</Typography>
              <Typography sx={{ color: '#ddd', fontSize: 13, flex: 1, minWidth: 180 }}>{p.referrer_name || p.referrer_tid} → <b>{p.invited_name || p.invited_tid}</b></Typography>
              <Typography sx={{ color: p.inviter_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>запрошувачу +{p.inviter_amount}</Typography>
              <Typography sx={{ color: p.invited_amount > 0 ? '#39e58c' : '#888', fontSize: 12.5 }}>· новачку +{p.invited_amount}</Typography>
              <Chip size="small" label={st[0]} sx={{ fontSize: 10.5, height: 20, color: '#fff', bgcolor: st[1] }} />
            </Box>);
        })}
        {pendingHint && <Typography sx={{ color: '#777', fontSize: 11.5, mt: .8 }}>{pendingHint}</Typography>}
      </>)}
    </Box>
  );
}

function NqReferralBlock({ referral, onSaved }) {
  const [vals, setVals] = useState({ ref_credits: '', ref_welcome_credits: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (referral) setVals({ ref_credits: String(referral.ref_credits), ref_welcome_credits: String(referral.ref_welcome_credits) });
  }, [referral]);
  if (!referral) return null;
  const put = async (key, value) => {
    const r = await APIJ('/settings', { key, value: String(value) });
    const d = await r.json();
    if (!(d && d.ok)) throw new Error((d && d.detail) || 'не збережено');
  };
  const toggle = async () => {
    setBusy(true); setMsg('');
    try { await put('referral_enabled', referral.enabled ? '0' : '1'); setMsg(referral.enabled ? '⏸ Вимкнено.' : '✅ Увімкнено.'); await onSaved(); }
    catch (e) { setMsg('❌ ' + e.message); }
    setBusy(false);
  };
  const save = async () => {
    const a = parseInt(vals.ref_credits, 10), b = parseInt(vals.ref_welcome_credits, 10);
    if (!(a >= 0) || !(b >= 0)) { setMsg('❌ Лише цілі числа від 0.'); return; }
    setBusy(true); setMsg('');
    try { await put('ref_credits', a); await put('ref_welcome_credits', b); setMsg('✅ Збережено — діє для наступних друзів.'); await onSaved(); }
    catch (e) { setMsg('❌ ' + e.message); }
    setBusy(false);
  };
  const dirty = String(referral.ref_credits) !== vals.ref_credits || String(referral.ref_welcome_credits) !== vals.ref_welcome_credits;
  const fld = { InputLabelProps: { sx: { color: '#9a92c4' } }, inputProps: { min: 0, style: { color: '#eee' } } };
  return (
    <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(242,178,60,.06)', border: '1px solid rgba(242,178,60,.25)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ color: '#f2b23c', fontWeight: 700 }}>🎁 Реферальний бонус</Typography>
        <FormControlLabel
          control={<Switch checked={!!referral.enabled} onChange={toggle} disabled={busy} size="small" color="warning" />}
          label={<span style={{ color: referral.enabled ? '#f2b23c' : '#888', fontSize: 13 }}>
            {referral.enabled ? 'Увімкнено' : 'Вимкнено — за друзів не нараховується нічого'}</span>} />
      </Stack>
      <Typography sx={{ color: '#aaa', fontSize: 13, mb: 1.2 }}>
        Коли НОВА людина відкриває NUMERIQ за запрошенням (кнопка «📤 Поділитися» або запрошення з розділу «Люди»),
        кредити отримують обоє. Лише за нового, не за себе, один раз за кожного друга. Тому, хто запросив, приходить
        повідомлення його мовою.
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1.5, mb: 1 }}>
        <TextField label="Тому, хто запросив" type="number" size="small" sx={{ width: 170 }} {...fld}
          value={vals.ref_credits} onChange={(e) => setVals({ ...vals, ref_credits: e.target.value })} />
        <TextField label="Новачку понад стартові" type="number" size="small" sx={{ width: 190 }} {...fld}
          value={vals.ref_welcome_credits} onChange={(e) => setVals({ ...vals, ref_welcome_credits: e.target.value })} />
        <Button size="small" variant="contained" disabled={busy || !dirty} onClick={save}
          sx={{ background: 'linear-gradient(120deg,#f2b23c,#43e2c0)', color: '#0d0c17', fontWeight: 700 }}>Зберегти</Button>
      </Stack>
      <Typography sx={{ color: '#888', fontSize: 12.5 }}>
        👥 Прийшло за запрошеннями: <b style={{ color: '#ddd' }}>{referral.invited}</b> · 💰 роздано бонусних кредитів: <b style={{ color: '#ddd' }}>{referral.credited}</b>
      </Typography>
      {msg && <Typography sx={{ mt: 1, color: msg.startsWith('❌') ? '#ff6b6b' : '#39e58c', fontSize: 13 }}>{msg}</Typography>}
      <NqRefPairs summary={referral.summary} pairs={referral.pairs} unit="кред."
        totals={{ pairs: (referral.pairs || []).length,
          earned: (referral.pairs || []).reduce((a, p) => a + p.inviter_amount, 0),
          invited_bonus: (referral.pairs || []).reduce((a, p) => a + p.invited_amount, 0) }} />
    </Box>
  );
}

function NqShareSection() {
  const [cfg, setCfg] = useState(null);
  const [lang, setLang] = useState('uk');
  const [edit, setEdit] = useState({ caption: {}, btn: {} });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [imgT, setImgT] = useState(Date.now());
  const load = useCallback(async () => {
    try {
      const r = await API('/share');
      const d = await r.json();
      if (d && d.ok) { setCfg(d); setEdit({ caption: { ...d.caption }, btn: { ...d.btn } }); }
      else setMsg('❌ Не вдалося завантажити налаштування: ' + ((d && d.detail) || r.status));
    } catch (e) { setMsg('❌ NUMERIQ недоступний: ' + e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const changedLang = (l) => !!cfg && ((edit.caption[l] || '') !== (cfg.caption[l] || '') || (edit.btn[l] || '') !== (cfg.btn[l] || ''));
  const dirty = !!cfg && NQ_SHARE_LANGS.some(([l]) => changedLang(l));
  const setField = (kind, val) => setEdit((p) => ({ ...p, [kind]: { ...p[kind], [lang]: val } }));
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await APIJ('/share', { caption: edit.caption, btn: edit.btn });
      const d = await r.json();
      if (d && d.ok) { setMsg('✅ Збережено — наступні запрошення підуть уже з цим текстом.'); await load(); }
      else setMsg('❌ ' + ((d && d.detail) || 'Не збережено'));
    } catch (e) { setMsg('❌ Помилка: ' + e.message); }
    setBusy(false);
  };
  const upload = async (e, scope) => {
    const input = e.target;
    const f = input.files && input.files[0];
    if (!f) return;
    setBusy(true); setMsg('');
    try {
      const blob = await nqShrinkImage(f);
      const fd = new FormData(); fd.append('file', blob, 'share.jpg'); fd.append('scope', scope);
      const r = await API('/share/image', { method: 'POST', body: fd });
      const d = await r.json();
      if (d && d.ok) { setMsg('✅ Картинку для ' + nqLangName(scope) + ' завантажено.'); setImgT(Date.now()); await load(); }
      else setMsg('❌ ' + ((d && d.detail) === 'not_image' ? 'Це не картинка.' : ((d && d.detail) || 'Не завантажено.')));
    } catch (err) { setMsg('❌ ' + err.message); }
    setBusy(false);
    input.value = '';
  };
  const remove = async (scope) => {
    const next = scope === 'all' ? 'мови без своєї картинки отримають намальовану картку'
      : (cfg && cfg.has_image ? 'для ' + nqLangName(scope) + ' буде спільна картинка' : 'для ' + nqLangName(scope) + ' буде намальована картка');
    if (!window.confirm('Прибрати картинку для ' + nqLangName(scope) + '? Після цього ' + next + '.')) return;
    setBusy(true); setMsg('');
    try {
      const r = await API('/share/image?scope=' + scope, { method: 'DELETE' });
      const d = await r.json();
      if (d && d.ok) { setMsg('✅ Картинку для ' + nqLangName(scope) + ' прибрано.'); setImgT(Date.now()); await load(); }
      else setMsg('❌ Не вдалося.');
    } catch (e) { setMsg('❌ Помилка: ' + e.message); }
    setBusy(false);
  };
  const paper = { p: 2, mb: 3, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' };
  const gold = { background: 'linear-gradient(120deg,#f2b23c,#43e2c0)', color: '#0d0c17', fontWeight: 700 };
  const field = { InputLabelProps: { sx: { color: '#9a92c4' } }, FormHelperTextProps: { sx: { color: '#777' } } };
  if (!cfg) return (
    <Paper sx={paper}><Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} />
      <Typography sx={{ color: '#aaa', fontSize: 14 }}>{msg || 'Завантаження…'}</Typography></Stack></Paper>);
  const src = (cfg.images || {})[lang] || {};
  const ln = nqLangName(lang);
  return (
    <Paper sx={paper}>
      <Typography sx={{ color: '#fff', mb: 1, fontSize: 18 }}>💌 Запрошення «Поділитися»</Typography>
      <Typography sx={{ color: '#aaa', mb: 2, fontSize: 14 }}>
        Так другові приходить запрошення, коли людина в застосунку тисне «📤 Поділитися» внизу. Текст і кнопка — окремо
        для кожної мови (мова того, хто ділиться). Кнопка завжди відкриває застосунок NUMERIQ із кодом того, хто запросив
        (для реферального бонусу) — це не змінюється.
      </Typography>
      <NqReferralBlock referral={cfg.referral} onSaved={load} />
      <Stack direction="row" spacing={.8} sx={{ mb: 2, flexWrap: 'wrap', rowGap: .8 }}>
        {NQ_SHARE_LANGS.map(([l, name]) => {
          const own = ((cfg.images || {})[l] || {}).source === 'lang';
          const custom = cfg.custom_caption[l] || cfg.custom_btn[l];
          return <Chip key={l} label={name + (own ? ' 🖼' : '') + (changedLang(l) ? ' •' : '')} onClick={() => setLang(l)}
            sx={{ bgcolor: lang === l ? 'rgba(242,178,60,.18)' : 'rgba(124,92,255,.12)',
              color: lang === l ? '#f2b23c' : (custom ? '#39e58c' : '#cbb8ff'),
              border: lang === l ? '1px solid #f2b23c' : '1px solid transparent', fontWeight: 600 }} />;
        })}
      </Stack>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ width: 320, maxWidth: '100%', flexShrink: 0 }}>
          <Typography sx={{ color: '#777', fontSize: 12, mb: .8 }}>Так побачить друг у Telegram:</Typography>
          <Box sx={{ bgcolor: '#182533', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,.06)' }}>
            <NqAuthImg src={'/api/dashboard/numeriq/share/img?lang=' + lang + '&t=' + imgT}
              style={{ width: '100%', display: 'block' }}
              fallback={<Box sx={{ p: 3, textAlign: 'center', color: '#8a83b0', fontSize: 13 }}>
                🎨 Картка малюється в застосунку мовою людини — зразок з'явиться після першого запрошення цією мовою.
              </Box>} />
            <Box sx={{ px: 1.5, py: 1.2, color: '#f5f5f5', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {edit.caption[lang] || cfg.default_caption[lang]}
            </Box>
          </Box>
          <Box sx={{ mt: .6, bgcolor: 'rgba(24,37,51,.85)', borderRadius: '10px', py: 1, textAlign: 'center',
            color: '#fff', fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,.06)' }}>
            {edit.btn[lang] || cfg.default_btn[lang]} ↗
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography sx={{ color: '#ddd', fontSize: 14, fontWeight: 600, mb: 1 }}>🖼 Картинка для {ln}</Typography>
          <Typography sx={{ color: '#aaa', fontSize: 13, mb: 1 }}>
            {src.source === 'lang' ? 'Зараз — своя картинка саме для ' + ln + (src.ts ? ' (завантажено ' + src.ts + ')' : '') + '.'
              : src.source === 'all' ? 'Зараз — спільна картинка для всіх мов. Можеш дати ' + ln + ' свою.'
                : 'Зараз — картка NUMERIQ, яку застосунок малює мовою людини (сузірʼя чисел, назва, 4 пункти).'}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            <Button variant="contained" component="label" disabled={busy} sx={gold}>
              {src.source === 'lang' ? 'Замінити для ' + ln : 'Завантажити для ' + ln}
              <input type="file" accept="image/*" hidden onChange={(e) => upload(e, lang)} />
            </Button>
            {src.source === 'lang' && (
              <Button variant="outlined" disabled={busy} onClick={() => remove(lang)}
                sx={{ color: '#cbb8ff', borderColor: 'rgba(150,130,255,.4)' }}>↺ Прибрати картинку {ln}</Button>
            )}
          </Stack>
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,92,255,.07)', border: '1px solid rgba(150,130,255,.15)' }}>
            <Typography sx={{ color: '#ddd', fontSize: 13, fontWeight: 600, mb: .5 }}>🌐 Спільна картинка для всіх мов</Typography>
            <Typography sx={{ color: '#aaa', fontSize: 12.5, mb: 1 }}>
              {cfg.has_image ? 'Є' + (cfg.image_ts ? ' (завантажено ' + cfg.image_ts + ')' : '') + ' — її отримують мови без своєї картинки.'
                : 'Немає — мови без своєї картинки отримують намальовану картку.'}
              {' '}Порядок: своя картинка мови → спільна → намальована. Найкраще — 4:5 (1080×1350) або квадрат.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button size="small" variant="outlined" component="label" disabled={busy}
                sx={{ color: '#f2b23c', borderColor: 'rgba(242,178,60,.45)' }}>
                {cfg.has_image ? 'Замінити спільну' : 'Завантажити спільну'}
                <input type="file" accept="image/*" hidden onChange={(e) => upload(e, 'all')} />
              </Button>
              {cfg.has_image && (
                <Button size="small" disabled={busy} onClick={() => remove('all')} sx={{ color: '#cbb8ff' }}>↺ Прибрати спільну</Button>
              )}
            </Stack>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ color: '#ddd', fontSize: 14, fontWeight: 600 }}>✍️ Текст ({ln})</Typography>
            <Chip size="small" label={cfg.custom_caption[lang] || cfg.custom_btn[lang] ? 'змінений' : 'стандартний'}
              sx={{ bgcolor: cfg.custom_caption[lang] || cfg.custom_btn[lang] ? 'rgba(57,229,140,.15)' : 'rgba(124,92,255,.15)',
                color: cfg.custom_caption[lang] || cfg.custom_btn[lang] ? '#39e58c' : '#cbb8ff' }} />
          </Stack>
          <TextField label="Текст під картинкою" size="small" fullWidth multiline minRows={5} {...field}
            value={edit.caption[lang] || ''} onChange={(e) => setField('caption', e.target.value)}
            inputProps={{ maxLength: cfg.caption_max, style: { color: '#eee' } }}
            helperText={(edit.caption[lang] || '').length + ' / ' + cfg.caption_max + ' символів · емодзі можна'} sx={{ mb: 1.5 }} />
          <TextField label="Напис на кнопці" size="small" fullWidth {...field}
            value={edit.btn[lang] || ''} onChange={(e) => setField('btn', e.target.value)}
            inputProps={{ maxLength: cfg.btn_max, style: { color: '#eee' } }}
            helperText={(edit.btn[lang] || '').length + ' / ' + cfg.btn_max} sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button variant="contained" disabled={busy || !dirty} onClick={save} sx={gold}>
              {busy ? 'Зберігаю…' : '💾 Зберегти всі мови'}
            </Button>
            <Button size="small" disabled={busy}
              onClick={() => setEdit((p) => ({ caption: { ...p.caption, [lang]: cfg.default_caption[lang] },
                btn: { ...p.btn, [lang]: cfg.default_btn[lang] } }))}
              sx={{ color: '#43e2c0' }}>↺ Стандартний текст для цієї мови</Button>
            {dirty && <Typography sx={{ color: '#f2b23c', fontSize: 13 }}>• є незбережені зміни</Typography>}
          </Stack>
          {msg && <Typography sx={{ mt: 1.5, color: msg.startsWith('✅') ? '#39e58c' : '#ff6b6b', fontSize: 14 }}>{msg}</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}

const SETTING_LABELS = {
  credits_per_eur: 'Курс: кредитів за €1',
  min_credits: 'Мінімум кредитів при поповненні',
  cost_full_reading: 'Ціна: повний розбір (0 = безкоштовно)',
  cost_number_deep: 'Ціна: глибокий розбір числа (0 = безкоштовно)',
  cost_destiny_point: 'Ціна: пункт Матриці долі — тлумачення однієї позиції (0 = безкоштовно)',
  cost_today: 'Ціна: розбір дня — один платіж на добу, відкриває всі 12 пунктів (0 = безкоштовно)',
  cost_applied: 'Ціна: прикладний розбір — номер, дата або нове ім\u02bcя; платиться за кожен новий ввід, повтор того самого — безкоштовно (0 = безкоштовно)',
  cost_kin: 'Ціна: пункт чужої матриці — дитячої або матриці пари; самі числа безкоштовні, платиться тлумачення одного пункту (0 = безкоштовно)',
  destiny_step_ms: 'Швидкість збірки Матриці долі (мс на крок; більше = повільніше й урочистіше)',
  cost_matrix: 'Ціна: Квадрат Піфагора (0 = безкоштовно)',
  cost_compat: 'Ціна: сумісність (0 = безкоштовно)',
  cost_scan: 'Ціна: сканер (0 = безкоштовно)',
  cost_chat: 'Ціна: повідомлення AI-чату (0 = безкоштовно)',
  starter_credits: 'Кредитів новому користувачу',
  paid_until: 'Оплачено до (дата)',
  constellation_node_delay_ms: 'Затримка появи вузла Карти Долі (мс)',
  music_volume_default: 'Гучність музики за замовчуванням (0-1)',
};

// Звук тягнемо ЧЕРЕЗ дашборд, а не напряму з домену NUMERIQ: CSP дашборду має
// `media-src 'self' … mediabot.uk` без нього, тож пряме посилання браузер тихо блокує.
// А оскільки <audio src> не надсилає токен авторизації, вантажимо файл авторизованим
// запитом у blob: — він у media-src дозволений. Той самий підхід, що для музики ZODIAQ.
const MUSIC_AUDIO = (fn) => '/api/dashboard/numeriq/music/audio?filename=' + encodeURIComponent(fn);

// Коди помилок бекенду → людські пояснення. `not_audio` з'явився після реального випадку:
// браузер надіслав файл правильного розміру, але з порожнім вмістом — трек зберігся й не грав.
const MUSIC_ERRORS = {
  not_audio: 'Це не аудіофайл або він порожній. Спробуй перезберегти трек локально й завантажити ще раз — так буває з файлами з хмарних тек, які ще не звантажені на диск.',
  bad_audio_type: 'Такий формат не підтримується. Потрібен mp3, ogg, m4a, aac або wav.',
  file_too_large: 'Файл більший за 12 МБ.',
  empty_file: 'Файл порожній.',
};

const CATEGORY_LABELS = {
  life_path: 'Число Долі',
  expression: 'Число Вираження',
  soul_urge: 'Число Душі',
  personality: 'Число Особистості',
  maturity: 'Число Зрілості',
  personal_year: 'Особистий рік',
  personal_month: 'Особистий місяць',
  personal_day: 'Особистий день',
  birthday_number: 'Число Народження',
};

const MEANING_LANGS = [
  { code: 'uk', flag: '🇺🇦', name: 'Українська' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'pl', flag: '🇵🇱', name: 'Polski' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
];

function MeaningsSection() {
  const [index, setIndex] = useState([]);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState('');
  const [category, setCategory] = useState('');
  const [number, setNumber] = useState(null);
  const [rowsLoad, setRowsLoad] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState('');
  const [suggestion, setSuggestion] = useState({});
  const [suggesting, setSuggesting] = useState('');

  useEffect(() => {
    API('/meanings/index').then(r => r.json()).then(d => {
      if (Array.isArray(d)) {
        setIndex(d);
        if (d.length) { setCategory(d[0].category); setNumber(d[0].number); }
      } else setErr('Помилка формату відповіді');
    }).catch(() => setErr('NUMERIQ недоступний')).finally(() => setLoad(false));
  }, []);

  const categories = [...new Set(index.map(i => i.category))];
  const numbersInCat = index.filter(i => i.category === category);

  const loadRows = useCallback((cat, num) => {
    if (!cat || num == null) return;
    setRowsLoad(true); setMsg('');
    API(`/meanings/${cat}/${num}`).then(r => r.json()).then(d => {
      if (Array.isArray(d)) {
        const dr = {};
        d.forEach(row => { dr[row.lang] = { short_text: row.short_text, full_text: row.full_text || '' }; });
        setDrafts(dr);
        setSuggestion({});
      }
    }).catch(() => setMsg('❌ Не вдалось завантажити тексти')).finally(() => setRowsLoad(false));
  }, []);

  useEffect(() => { loadRows(category, number); }, [category, number, loadRows]);

  const setDraft = (lang, field, value) => {
    setDrafts(p => ({ ...p, [lang]: { ...p[lang], [field]: value } }));
  };

  const save = async (lang) => {
    const d = drafts[lang];
    if (!d || !d.short_text || !d.short_text.trim()) { setMsg('❌ Короткий текст не може бути порожнім'); return; }
    setSaving(lang); setMsg('');
    try {
      const r = await APIJ(`/meanings/${category}/${number}/${lang}`, d, 'PUT');
      const dd = await r.json().catch(() => null);
      if (r.ok) setMsg(`✅ Збережено (${lang})`);
      else setMsg('❌ ' + ((dd && dd.detail) || `сервер відповів HTTP ${r.status}`));
    } catch (_e) {
      setMsg('❌ Не вдалось звʼязатися з сервером');
    } finally {
      setSaving('');
    }
  };

  const suggest = async (lang) => {
    setSuggesting(lang); setMsg('');
    try {
      const r = await API(`/meanings/${category}/${number}/${lang}/suggest`, { method: 'POST' });
      const d = await r.json().catch(() => null);
      if (r.ok && d) setSuggestion(p => ({ ...p, [lang]: d }));
      else setMsg('❌ ' + ((d && d.detail) || `сервер відповів HTTP ${r.status}`));
    } catch (_e) {
      setMsg('❌ Не вдалось звʼязатися з ШІ');
    } finally {
      setSuggesting('');
    }
  };

  const useSuggestion = (lang) => {
    const sug = suggestion[lang];
    if (!sug) return;
    setDraft(lang, 'short_text', sug.short_text);
    setDraft(lang, 'full_text', sug.full_text);
    setSuggestion(p => { const n = { ...p }; delete n[lang]; return n; });
  };

  const dropSuggestion = (lang) => {
    setSuggestion(p => { const n = { ...p }; delete n[lang]; return n; });
  };

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
      <Typography sx={{ color: '#fff', mb: 1 }}>✏️ Тлумачення чисел</Typography>
      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>
        Обери категорію і число — нижче зʼявиться текст усіма 8 мовами. Кожна мова зберігається окремою кнопкою.
      </Typography>

      {load ? <CircularProgress size={20} /> : err ? (
        <Alert severity="error">{err}</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Категорія" value={category}
              onChange={e => {
                const cat = e.target.value;
                setCategory(cat);
                const first = index.find(i => i.category === cat);
                setNumber(first ? first.number : null);
              }}
              sx={{ minWidth: 220, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#888' } }}
              SelectProps={{ native: true }}>
              {categories.map(c => <option key={c} value={c} style={{ color: '#111' }}>{CATEGORY_LABELS[c] || c}</option>)}
            </TextField>
            <TextField select size="small" label="Число" value={number ?? ''}
              onChange={e => setNumber(Number(e.target.value))}
              sx={{ minWidth: 340, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#888' } }}
              SelectProps={{ native: true }}>
              {numbersInCat.map(i => {
                const preview = (i.short_text || '').slice(0, 40);
                return (
                  <option key={i.number} value={i.number} style={{ color: '#111' }}>
                    {i.number} — {preview}{(i.short_text || '').length > 40 ? '…' : ''}
                  </option>
                );
              })}
            </TextField>
          </Stack>

          {msg && <Alert severity={msg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

          {rowsLoad ? <CircularProgress size={20} /> : (
            <Stack spacing={1.5}>
              {MEANING_LANGS.map(({ code, flag, name }) => {
                const d = drafts[code] || { short_text: '', full_text: '' };
                return (
                  <Box key={code} sx={{ p: 1.5, border: '1px solid #2a2a4a', borderRadius: 1 }}>
                    <Typography sx={{ color: '#aaa', fontSize: 13, mb: 0.5 }}>{flag} {name}</Typography>
                    <TextField fullWidth size="small" label="Короткий текст"
                      value={d.short_text} onChange={e => setDraft(code, 'short_text', e.target.value)}
                      sx={{ mb: 1, input: { color: '#fff' }, '& .MuiInputLabel-root': { color: '#888' } }} />
                    <TextField fullWidth size="small" label="Повний текст" multiline minRows={3} maxRows={10}
                      value={d.full_text} onChange={e => setDraft(code, 'full_text', e.target.value)}
                      sx={{ mb: 1, textarea: { color: '#fff' }, '& .MuiInputLabel-root': { color: '#888' } }} />
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" disabled={saving === code}
                        onClick={() => save(code)}>
                        {saving === code ? 'Зберігаю…' : 'Зберегти'}
                      </Button>
                      <Button size="small" variant="text" disabled={suggesting === code}
                        onClick={() => suggest(code)} sx={{ color: '#b39ddb' }}>
                        {suggesting === code ? '🤖 Думаю…' : '🤖 Покращити ШІ'}
                      </Button>
                    </Stack>
                    {suggestion[code] && (
                      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(179,157,219,0.08)', border: '1px solid var(--ac1, #7c4dff)', borderRadius: 1 }}>
                        <Typography sx={{ color: '#b39ddb', fontSize: 12, mb: 0.5 }}>🤖 Пропозиція ШІ (порівняй зі своїм варіантом вище):</Typography>
                        <Typography sx={{ color: '#ddd', fontSize: 13, mb: 0.5, fontWeight: 'bold' }}>{suggestion[code].short_text}</Typography>
                        <Typography sx={{ color: '#bbb', fontSize: 13, whiteSpace: 'pre-wrap', mb: 1 }}>{suggestion[code].full_text}</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" onClick={() => useSuggestion(code)}
                            sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>✅ Використати</Button>
                          <Button size="small" variant="text" onClick={() => dropSuggestion(code)}
                            sx={{ color: '#888' }}>❌ Відхилити</Button>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}

function PaymentsSection() {
  const [nq, setNq] = useState({});          // numeriq_settings ('1'/'0')
  const [mono, setMono] = useState(false);   // referral_settings ('true'/'false')
  const [load, setLoad] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const reload = useCallback(() => {
    setLoad(true);
    Promise.all([
      API('/settings').then(r => r.json()).catch(() => []),
      fetch('/api/referral/settings', { headers: AUTH() }).then(r => r.json()).catch(() => ({})),
    ]).then(([nqRows, ref]) => {
      const m = {};
      (Array.isArray(nqRows) ? nqRows : []).forEach(r => { m[r.key] = r.value; });
      setNq(m);
      setMono(String(ref.numeriq_mono_enabled || '').toLowerCase() === 'true');
    }).finally(() => setLoad(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const on = (k) => ['1', 'true', 'yes', 'on'].includes(String(nq[k] || '').toLowerCase());

  const toggleNq = async (key) => {
    const next = on(key) ? '0' : '1';
    setBusy(key); setMsg('');
    try {
      const r = await APIJ('/settings', { key, value: next });
      if (r.ok) { setNq(p => ({ ...p, [key]: next })); setMsg('✅ Збережено'); }
      else setMsg('❌ Не вдалось зберегти');
    } catch (_e) { setMsg('❌ Помилка мережі'); }
    finally { setBusy(''); }
  };

  const toggleMono = async () => {
    const next = mono ? 'false' : 'true';
    setBusy('mono'); setMsg('');
    try {
      const r = await fetch('/api/referral/settings', {
        method: 'POST',
        headers: { ...AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ numeriq_mono_enabled: next }),
      });
      if (r.ok) { setMono(!mono); setMsg('✅ Збережено'); }
      else setMsg('❌ Не вдалось зберегти');
    } catch (_e) { setMsg('❌ Помилка мережі'); }
    finally { setBusy(''); }
  };

  const Row = ({ label, hint, checked, onChange, disabled }) => (
    <Box sx={{ mb: 1.5 }}>
      <FormControlLabel
        control={<Switch checked={checked} onChange={onChange} disabled={disabled} />}
        label={<span style={{ color: '#ccc' }}>{label}</span>}
      />
      <Typography sx={{ color: '#888', fontSize: 11, ml: 6 }}>{hint}</Typography>
    </Box>
  );

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
      <Typography sx={{ color: '#fff', mb: 1 }}>💳 Способи оплати</Typography>
      <Typography sx={{ color: '#888', fontSize: 12, mb: 1.5 }}>
        Вимкнений спосіб зникає з застосунку одразу — юзер його не побачить.
      </Typography>
      {msg && <Alert severity={msg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}
      {load ? <CircularProgress size={20} /> : (
        <>
          <Row label="Картка онлайн (Stripe)" checked={on('pay_show_card')}
            onChange={() => toggleNq('pay_show_card')} disabled={busy === 'pay_show_card'}
            hint="Спільний акаунт MediaSystemGroup. Кредити нараховуються самі, виплата на Revolut раз на місяць." />
          <Row label="Monobank (банка)" checked={mono}
            onChange={toggleMono} disabled={busy === 'mono'}
            hint="Потрібен jar-лінк у «💎 Реферальна система». Зарахування автоматичне, спільний поллер." />
          <Row label="Revolut вручну (чек)" checked={on('pay_show_revolut')}
            onChange={() => toggleNq('pay_show_revolut')} disabled={busy === 'pay_show_revolut'}
            hint="Юзер вантажить чек, вам приходить фото в Telegram з кнопками. Потрібні реквізити revolut_recipient нижче." />
        </>
      )}
    </Paper>
  );
}

function MusicSection() {
  const [tracks, setTracks] = useState([]);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState({});
  const [audio, setAudio] = useState({});   // id -> blob-URL, створюється лише на запит
  const inp = useRef(null);

  // blob-URL живуть до явного revoke — прибираємо за собою, щоб не текла пам'ять
  useEffect(() => () => Object.values(audio).forEach(u => URL.revokeObjectURL(u)), [audio]);

  const listen = async (t) => {
    if (audio[t.id]) return;
    try {
      const r = await fetch(MUSIC_AUDIO(t.filename), { headers: AUTH() });
      if (!r.ok) { setMsg(`❌ Не вдалось завантажити трек (HTTP ${r.status})`); return; }
      const url = URL.createObjectURL(await r.blob());
      setAudio(p => ({ ...p, [t.id]: url }));
    } catch (_e) {
      setMsg('❌ Не вдалось завантажити трек');
    }
  };

  const reload = useCallback(() => {
    setLoad(true); setErr('');
    API('/music').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setTracks(d);
      else setErr('Помилка формату відповіді');
    }).catch(() => setErr('NUMERIQ недоступний')).finally(() => setLoad(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('title', f.name.replace(/\.[^.]+$/, ''));
      // ВАЖЛИВО: лише AUTH() — без 'Content-Type', інакше браузер не проставить boundary
      const r = await fetch('/api/dashboard/numeriq/music', { method: 'POST', headers: AUTH(), body: fd });
      let d = null;
      try { d = await r.json(); } catch (_p) { /* не-JSON — покажемо статус нижче */ }
      if (d && d.id) { setMsg('✅ Трек додано: ' + (d.title || d.filename)); reload(); }
      else {
        const code = d && d.detail;
        setMsg('❌ ' + (MUSIC_ERRORS[code] || code || `сервер відповів HTTP ${r.status}`));
      }
    } catch (_e) {
      setMsg('❌ Не вдалось звʼязатися з сервером');
    } finally {
      setBusy(false);
      if (inp.current) inp.current.value = '';
    }
  };

  const toggle = (t) => {
    API(`/music/${t.id}/toggle`, { method: 'POST' })
      .then(() => reload()).catch(() => setMsg('❌ Не вдалось перемкнути'));
  };

  const rename = (t) => {
    const fd = new FormData();
    fd.append('title', names[t.id] ?? t.title ?? '');
    fetch(`/api/dashboard/numeriq/music/${t.id}/name`, { method: 'POST', headers: AUTH(), body: fd })
      .then(() => { setMsg('✅ Назву збережено'); reload(); })
      .catch(() => setMsg('❌ Не вдалось перейменувати'));
  };

  const del = (t) => {
    if (!window.confirm(`Видалити трек «${t.title || t.filename}» назавжди?`)) return;
    API(`/music/${t.id}`, { method: 'DELETE' })
      .then(() => { setMsg('🗑 Трек видалено'); reload(); })
      .catch(() => setMsg('❌ Не вдалось видалити'));
  };

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ color: '#fff' }}>🎵 Музика міні-застосунку</Typography>
        <Button size="small" variant="contained" disabled={busy}
          onClick={() => inp.current && inp.current.click()}>
          {busy ? 'Завантажую…' : '⬆️ Завантажити трек'}
        </Button>
      </Stack>
      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>
        mp3 · ogg · m4a · aac · wav, до 12 МБ. Увімкнені треки застосунок обирає випадково при старті.
      </Typography>
      <input ref={inp} type="file" accept="audio/*,.mp3,.ogg,.m4a,.aac,.wav"
        style={{ display: 'none' }} onChange={upload} />

      {msg && <Alert severity={msg.startsWith('✅') || msg.startsWith('🗑') ? 'success' : 'error'}
        sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {load ? <CircularProgress size={20} /> : err ? (
        <Alert severity="error">{err}</Alert>
      ) : tracks.length === 0 ? (
        <Typography sx={{ color: '#666' }}>
          Ще жодного треку — у застосунку зараз тиша. Завантаж перший файл кнопкою вище.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['ID', 'Назва', 'Прослухати', 'Увімкнено', ''].map(h => (
                  <TableCell key={h} sx={{ color: '#888', borderColor: '#2a2a4a' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tracks.map(t => (
                <TableRow key={t.id}>
                  <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{t.id}</TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <TextField size="small" defaultValue={t.title || ''}
                        onChange={e => setNames(p => ({ ...p, [t.id]: e.target.value }))}
                        sx={{ input: { color: '#fff' } }} />
                      <Button size="small" variant="outlined" onClick={() => rename(t)}>Зберегти</Button>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    {audio[t.id]
                      ? <audio controls autoPlay src={audio[t.id]} style={{ height: 32 }} />
                      : <Button size="small" variant="outlined" onClick={() => listen(t)}>▶️ Прослухати</Button>}
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Switch checked={!!t.active} onChange={() => toggle(t)} />
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Button size="small" color="error" onClick={() => del(t)}>🗑</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function StatCard({ label, value, sub, color = '#e6c34d' }) {
  return (
    <Paper sx={{ p: 2, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a', minWidth: 150, flex: 1 }}>
      <Typography sx={{ color: '#888', fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color, fontSize: 28, fontWeight: 'bold' }}>{value ?? '—'}</Typography>
      {sub && <Typography sx={{ color: '#666', fontSize: 11 }}>{sub}</Typography>}
    </Paper>
  );
}

export default function NumeriqPage() {
  const [settings, setSettings] = useState([]);
  const [settingsErr, setSettingsErr] = useState('');
  const [settingsLoad, setSettingsLoad] = useState(true);
  const [edited, setEdited] = useState({});
  const [saveMsg, setSaveMsg] = useState('');

  const [users, setUsers] = useState([]);
  const [usersErr, setUsersErr] = useState('');
  const [usersLoad, setUsersLoad] = useState(true);
  // 🔢 2026-09-11: 🔴 нові (ще не переглянуті) юзери — гасне по кліку на рядок
  const [newCount, setNewCount] = useState(0);

  const [creditDlg, setCreditDlg] = useState(null);   // = картка юзера
  const [creditInput, setCreditInput] = useState('');
  const [creditMsg, setCreditMsg] = useState('');
  const [giftNotify, setGiftNotify] = useState(true); // 🎉 вітання у бот
  const [giftBusy, setGiftBusy] = useState(false);
  const [tab, setTab] = useState(0);

  const loadSettings = useCallback(() => {
    setSettingsLoad(true); setSettingsErr('');
    API('/settings').then(r => r.json()).then(d => {
      if (Array.isArray(d)) setSettings(d);
      else setSettingsErr('Помилка формату відповіді');
    }).catch(() => setSettingsErr('NUMERIQ недоступний')).finally(() => setSettingsLoad(false));
  }, []);

  const loadUsers = useCallback(() => {
    setUsersLoad(true); setUsersErr('');
    API('/users').then(r => r.json()).then(d => {
      if (Array.isArray(d)) { setUsers(d); setNewCount(d.filter(u => u.is_new).length); }
      else setUsersErr('Помилка формату відповіді');
    }).catch(() => setUsersErr('NUMERIQ недоступний')).finally(() => setUsersLoad(false));
  }, []);

  // Позначити переглянутим: конкретного юзера (клік по рядку) або всіх одразу.
  // Рядок лишається підсвіченим до перезавантаження — щоб не зникав з-під руки.
  const markSeen = useCallback((tgId, all = false) => {
    APIJ('/user-seen', all ? { all: true } : { tg_id: tgId })
      .then(() => { setNewCount(n => all ? 0 : Math.max(0, n - 1)); })
      .catch(() => {});
    setUsers(us => us.map(u => (all || u.tg_id === tgId) ? { ...u, is_new: false, _wasNew: u.is_new || u._wasNew } : u));
  }, []);

  useEffect(() => { loadSettings(); loadUsers(); }, [loadSettings, loadUsers]);

  const settingMap = {};
  settings.forEach(s => { settingMap[s.key] = s.value; });
  const monetizationOn = settingMap.master_monetization_enabled === '1';

  const saveSetting = (key, value) => {
    setSaveMsg('');
    APIJ('/settings', { key, value: String(value) }).then(r => r.json()).then(() => {
      setSaveMsg('✅ Збережено: ' + key);
      loadSettings();
    }).catch(() => setSaveMsg('❌ Помилка збереження'));
  };

  const toggleMonetization = (e) => saveSetting('master_monetization_enabled', e.target.checked ? '1' : '0');

  const openCreditDlg = (u) => { setCreditDlg(u); setCreditInput('10'); setCreditMsg(''); };
  const grantCredits = () => {
    const delta = parseInt(creditInput, 10);
    if (!delta) { setCreditMsg('❌ Введи число (від\'ємне = тиха корекція)'); return; }
    setGiftBusy(true); setCreditMsg('…');
    APIJ('/credits', { user_id: creditDlg.id, delta, note: 'dashboard_manual', notify: giftNotify })
      .then(r => r.json()).then(d => {
        if (d && typeof d.balance === 'number') {
          let m = (delta > 0 ? '✅ Подаровано ' + delta : '✅ Знято ' + (-delta)) + ' · баланс: ' + d.balance;
          // Показуємо ЩО САМЕ сталося з вітанням: «нараховано» без цього рядка
          // виглядає як успіх навіть тоді, коли юзер нічого не отримав.
          if (delta > 0 && giftNotify) {
            m += d.notified
              ? ' · вітання надіслано' + (d.notify_how === 'photo' ? ' 🖼' : ' (текстом)') + (d.notify_effect ? ' 🎉' : '')
              : ' · ⚠️ вітання НЕ дійшло: ' + (d.notify_error || 'невідома причина');
          } else if (delta > 0) m += ' · без вітання';
          setCreditMsg(m);
          setCreditDlg(u => (u ? { ...u, credits: d.balance } : u));
          loadUsers();
        } else setCreditMsg('❌ ' + (d.detail || 'помилка'));
      }).catch(() => setCreditMsg('❌ Помилка мережі')).finally(() => setGiftBusy(false));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ color: '#fff', mb: 1 }}>🔢 NUMERIQ</Typography>
      <Typography sx={{ color: '#888', mb: 2 }}>
        Нумерологія mini-app · <Link href="https://numeriq.167.233.172.34.sslip.io/" target="_blank" sx={{ color: '#7dfaff' }}>відкрити застосунок</Link>
        {' · '}
        <Link href="https://numeriq.167.233.172.34.sslip.io/instruction.html" target="_blank" sx={{ color: '#7dfaff' }}>інструкція</Link>
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <StatCard label="Юзерів" value={users.length} />
        <StatCard label="Монетизація" value={monetizationOn ? 'ON' : 'OFF'} color={monetizationOn ? '#7dfaff' : '#888'} />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        TabIndicatorProps={{ style: { backgroundColor: 'var(--ac1, #7c4dff)' } }}
        sx={{ mb: 2, '& .MuiTab-root': { color: '#888' }, '& .Mui-selected': { color: 'var(--ac1, #7c4dff)' } }}>
        <Tab label="📊 Огляд" />
        <Tab label="🎵 Музика" />
        <Tab label="✏️ Тлумачення" />
        <Tab label={<Badge badgeContent={newCount} color="error" max={99}
                           sx={{ '& .MuiBadge-badge': { right: -10, top: 4 } }}>👤 Юзери</Badge>} />
        <Tab label="💌 Запрошення" />
      </Tabs>
      <AppQuickNav />

      {tab === 0 && (
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
        <Typography sx={{ color: '#fff', mb: 1 }}>💰 Кредити і монетизація</Typography>
        <FormControlLabel
          control={<Switch checked={monetizationOn} onChange={toggleMonetization} />}
          label={<span style={{ color: '#ccc' }}>Монетизація увімкнена (дефолт OFF — усе безкоштовно)</span>}
        />
        {saveMsg && <Alert severity="info" sx={{ mt: 1 }} onClose={() => setSaveMsg('')}>{saveMsg}</Alert>}
        {settingsLoad ? <CircularProgress size={20} sx={{ mt: 2 }} /> : settingsErr ? (
          <Alert severity="error" sx={{ mt: 2 }}>{settingsErr}</Alert>
        ) : (
          <Table size="small" sx={{ mt: 2 }}>
            <TableBody>
              {settings.filter(s => !['master_monetization_enabled','constellation_speed_editable','music_enabled','topup_secret','checkout_bridge_secret','referral_enabled','ref_credits','ref_welcome_credits'].includes(s.key)).map(s => (
                <TableRow key={s.key}>
                  <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{SETTING_LABELS[s.key] || s.key}</TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a', width: 160 }}>
                    <TextField
                      size="small" defaultValue={s.value}
                      onChange={e => setEdited(prev => ({ ...prev, [s.key]: e.target.value }))}
                      sx={{ input: { color: '#fff' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderColor: '#2a2a4a' }}>
                    <Button size="small" variant="outlined"
                      onClick={() => saveSetting(s.key, edited[s.key] ?? s.value)}>
                      Зберегти
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      )}

      {tab === 0 && <PaymentsSection />}

      {tab === 1 && <MusicSection />}

      {tab === 2 && <MeaningsSection />}

      {tab === 4 && <NqShareSection />}

      {tab === 3 && (
      <Paper sx={{ p: 2, bgcolor: 'rgba(26,26,46,0.8)', border: '1px solid #2a2a4a' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ color: '#fff' }}>👤 Юзери</Typography>
          {newCount > 0 && <Chip size="small" label={'🔴 ' + newCount + ' нових'}
            sx={{ bgcolor: 'rgba(255,107,107,.2)', color: '#ff6b6b', fontWeight: 700 }} />}
          {newCount > 0 && <Button size="small" onClick={() => markSeen(null, true)} sx={{ color: '#9a92c4' }}>Позначити всі</Button>}
        </Box>
        {usersLoad ? <CircularProgress size={20} /> : usersErr ? (
          <Alert severity="error">{usersErr}</Alert>
        ) : users.length === 0 ? (
          <Typography sx={{ color: '#666' }}>Ще немає жодного юзера.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['ID', 'Ім\'я', 'Username', 'Мова', 'Дата народж.', '💰 Кредити', 'Створено', ''].map(h => (
                    <TableCell key={h} sx={{ color: '#888', borderColor: '#2a2a4a' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} hover
                    onClick={() => { if (u.is_new) markSeen(u.tg_id); openCreditDlg(u); }}
                    sx={{ bgcolor: (u.is_new || u._wasNew) ? 'rgba(255,107,107,.10)' : 'transparent',
                          cursor: 'pointer' }}>
                    <TableCell sx={{ color: (u.is_new || u._wasNew) ? '#ff9a9a' : '#ccc', borderColor: '#2a2a4a',
                                     fontWeight: (u.is_new || u._wasNew) ? 700 : 400 }}>
                      {u.is_new ? '🔴 ' : ''}{u.id}
                    </TableCell>
                    <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{u.full_name || u.first_name || '—'}</TableCell>
                    <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{u.username ? '@' + u.username : '—'}</TableCell>
                    <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{u.lang}</TableCell>
                    <TableCell sx={{ color: '#ccc', borderColor: '#2a2a4a' }}>{u.birth_date || '—'}</TableCell>
                    <TableCell sx={{ borderColor: '#2a2a4a' }}>
                      <Chip label={u.credits} size="small" sx={{ bgcolor: 'rgba(230,195,77,0.15)', color: '#e6c34d' }} />
                    </TableCell>
                    <TableCell sx={{ color: '#888', borderColor: '#2a2a4a', fontSize: 12 }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={{ borderColor: '#2a2a4a' }}>
                      <Button size="small" variant="outlined" onClick={() => openCreditDlg(u)}>🪙 Картка</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      )}

      {/* 🔢 2026-09-11: картка юзера — усе, що про людину відомо, і подарунок звідти ж.
          Раніше клік по рядку не робив нічого, а кредити нараховувались «наосліп». */}
      <Dialog open={!!creditDlg} onClose={() => setCreditDlg(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#141428', border: '1px solid #2a2a4a', borderRadius: 2 } }}>
        {creditDlg && (<>
          <DialogTitle sx={{ color: '#fff', pb: 1 }}>
            {creditDlg.full_name || creditDlg.first_name || '—'}
            {creditDlg.username ? ' · @' + creditDlg.username : ''}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.1} sx={{ mt: .5 }}>
              {[
                ['🆔 Telegram ID', creditDlg.tg_id || '—'],
                ['#️⃣ ID у базі', String(creditDlg.id)],
                ['🌐 Мова', creditDlg.lang || '—'],
                ['🎂 Дата народження', creditDlg.birth_date || 'не вказано'],
                ['🪙 Кредитів', String(creditDlg.credits != null ? creditDlg.credits : '—')],
                ['📅 Реєстрація', creditDlg.created_at ? new Date(creditDlg.created_at).toLocaleString('uk-UA') : '—'],
              ].map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between"
                  sx={{ borderBottom: '1px solid #2a2a4a', pb: .7 }}>
                  <Typography sx={{ color: '#888', fontSize: 13.5 }}>{k}</Typography>
                  <Typography sx={{ color: '#eee', fontSize: 13.5, textAlign: 'right' }}>{v}</Typography>
                </Stack>
              ))}
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'rgba(230,195,77,.07)', border: '1px solid rgba(230,195,77,.28)' }}>
                <Typography sx={{ color: '#e6c34d', fontWeight: 700, fontSize: 14, mb: 1 }}>🎁 Подарувати кредити</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {[5, 10, 25, 50].map(n => (
                    <Chip key={n} label={'+' + n} size="small" clickable onClick={() => setCreditInput(String(n))}
                      sx={{ bgcolor: String(n) === String(creditInput) ? 'rgba(230,195,77,.28)' : 'rgba(var(--ac1rgb, 124, 77, 255),.15)',
                        color: String(n) === String(creditInput) ? '#e6c34d' : '#cbb8ff',
                        fontWeight: String(n) === String(creditInput) ? 700 : 400 }} />
                  ))}
                  <TextField size="small" type="number" value={creditInput}
                    onChange={e => setCreditInput(e.target.value)}
                    inputProps={{ style: { color: '#fff', width: 66, padding: '6px 8px' } }}
                    sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a4a' } }} />
                  <Button size="small" variant="contained" disabled={giftBusy} onClick={grantCredits}
                    sx={{ bgcolor: '#e6c34d', color: '#1a1a2e', fontWeight: 700, '&:hover': { bgcolor: '#f0d270' } }}>
                    {giftBusy ? '…' : '🎁 Подарувати'}
                  </Button>
                </Stack>
                <FormControlLabel sx={{ mt: .5, ml: 0 }}
                  control={<Switch size="small" checked={giftNotify} onChange={e => setGiftNotify(e.target.checked)} color="warning" />}
                  label={<Typography sx={{ color: '#888', fontSize: 12.5 }}>🎉 святкове вітання у бот (картка + ефект)</Typography>} />
                <Typography sx={{ color: '#666', fontSize: 11.5 }}>
                  Від'ємне число — тиха корекція балансу, без вітання.
                </Typography>
                {creditMsg && (
                  <Typography sx={{ mt: .8, fontSize: 13, color: creditMsg.startsWith('✅') ? '#4caf50' : (creditMsg === '…' ? '#888' : '#f44336') }}>
                    {creditMsg}
                  </Typography>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreditDlg(null)} sx={{ color: 'var(--ac1, #7c4dff)' }}>Закрити</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </Box>
  );
}
